import { PRICE_TYPES } from 'shared/consts/productTemplates.js'
import getImageUrl from 'views/components/organisms/AlgoliaInstantSearch/Algolia/getImageUrl'
import { getAlgoliaUserDataProductsByType } from 'views/components/organisms/AlgoliaInstantSearch/Algolia/helpers'
import { hasPersonalVariantAttributes } from 'views/components/organisms/FilteredProductList/SearchProductListItem/helpers'
import {
  findCheapestPriceFromMVProduct,
  isAnyProductVariantAvailable,
  isMultiVariantProduct,
} from 'views/utils/multiVariantUtils'

export function calculateRetailPrice(amount) {
  return amount / 100
}

const processElasticSuggestion = ({ price, id, ...rest }) => {
  const { retailPrice: { amount } = {} } = price || {}
  return {
    ...rest,
    id,
    deeplink: `products/${id}`,
    prices: [
      {
        amount,
        type: PRICE_TYPES.RETAIL,
      },
    ],
  }
}

/**
 * Add additional URL parameters to the deeplink
 */
const processAlgoliaDeeplink = ({
  queryID,
  query,
  position,
  isVariantAttributesExperimentEnabled,
  product: { pzn, id, deeplink, variants, variant_attributes } = {},
}) => {
  const personalVariantAttributesQueryParameter =
    isVariantAttributesExperimentEnabled &&
    hasPersonalVariantAttributes(variant_attributes) &&
    isAnyProductVariantAvailable({ variants, variant_attributes })
      ? ['master=true']
      : []
  const objectId = id ?? pzn
  const params = [
    `query=${query}`,
    `queryID=${queryID}`,
    `objectIDs=[${objectId}]`,
    `position=${position + 1}`,
    `eventName=${decodeURIComponent('click on product in suggest')}`,
    'eventType=click',
    ...personalVariantAttributesQueryParameter,
  ].join('&')

  return `${deeplink}?${params}`
}

const processAlgoliaSuggestion = props => {
  const {
    product,
    product: {
      productName: name,
      pzn,
      id,
      price,
      stockStatus,
      listPrice,
      discountInPercent,
      averageRating,
      prescriptionMedicine,
    },
    cdnUrl,
    isVariantAttributesExperimentEnabled,
    isRebrandingEnabled,
    highlightedProducts = [],
    placeholderFromCdn,
  } = props

  const isMVProduct =
    isMultiVariantProduct(product, isVariantAttributesExperimentEnabled) &&
    isAnyProductVariantAvailable(product)

  const cheapestPrice = isMVProduct
    ? findCheapestPriceFromMVProduct(product)
    : {
        retailPrice: price,
        listPrice,
        discountInPercent,
      }

  const calcPrice = cheapestPrice.retailPrice
  const cheapestListPrice = cheapestPrice.listPrice
  const cheapestDiscountInPercent = cheapestPrice.discountInPercent

  const highlightedLabel =
    highlightedProducts?.[id] || highlightedProducts?.[pzn]

  return {
    id: pzn ?? id,
    deeplink: processAlgoliaDeeplink(props),
    averageRating,
    discountInPercent: cheapestDiscountInPercent,
    listPriceFormatted:
      cheapestListPrice && calculateRetailPrice(cheapestListPrice),
    name,
    hasMultipleVariants: isMVProduct,
    highlightedLabel,
    prescriptionMedicine,
    prices: [
      {
        amount: calcPrice && calculateRetailPrice(calcPrice),
        type: PRICE_TYPES.RETAIL,
      },
    ],
    stockStatus: { status: stockStatus },
    thumbnailURL: getImageUrl({
      product,
      cdnUrl,
      isRebrandingEnabled,
      placeholderFromCdn,
    }),
  }
}

export function mapEStoSuggestResults({ content }) {
  return {
    suggestions: content.map(processElasticSuggestion),
  }
}

export function mapAlgoliaToSuggestResults({
  hits: products,
  query,
  queryID,
  abTestID,
  abTestVariantID,
  cdnUrl,
  isVariantAttributesExperimentEnabled,
  isRebrandingEnabled,
  userData,
  placeholderFromCdn,
}) {
  const highlightedProducts = getAlgoliaUserDataProductsByType(
    userData,
    'highlighted'
  )
  return {
    suggestions: products.map((product, position) =>
      processAlgoliaSuggestion({
        queryID,
        query,
        product,
        position,
        cdnUrl,
        isVariantAttributesExperimentEnabled,
        isRebrandingEnabled,
        highlightedProducts,
        placeholderFromCdn,
      })
    ),
    abTestID,
    abTestVariantID,
    redirectData: userData ? userData[0]?.redirects : [],
    withHighlightedProduct: Object.keys(highlightedProducts).length > 0,
  }
}

export function processAlgoliaQuerySuggestions({ hits: querySuggestions }) {
  let querySuggestionsArray = []

  querySuggestions.map(querySuggestion => {
    const suggestion = querySuggestion.query
    querySuggestionsArray.push(
      suggestion.charAt(0).toUpperCase() + suggestion.slice(1)
    )
  })

  return querySuggestionsArray
}
